import { useState, useEffect, useCallback } from 'react';

// Fallback rate if API fails (approximate SOL price)
const FALLBACK_SOL_USD = 130;

// Refresh every 60 seconds
const REFRESH_INTERVAL_MS = 60000;

interface SolPriceState {
  price: number;
  lastUpdated: Date | null;
  isLoading: boolean;
  error: string | null;
}

export function useSolPrice() {
  const [state, setState] = useState<SolPriceState>({
    price: FALLBACK_SOL_USD,
    lastUpdated: null,
    isLoading: true,
    error: null
  });

  const fetchPrice = useCallback(async () => {
    try {
      // Use CoinGecko simple price API (free, no key required)
      const response = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd',
        { signal: AbortSignal.timeout(5000) }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch SOL price');
      }

      const data = await response.json();
      const solPrice = data?.solana?.usd;

      if (solPrice && solPrice > 0) {
        setState({
          price: solPrice,
          lastUpdated: new Date(),
          isLoading: false,
          error: null
        });
        return solPrice;
      } else {
        throw new Error('Invalid price data');
      }
    } catch (error) {
      console.warn('[useSolPrice] Failed to fetch, using fallback:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }));
      return state.price;
    }
  }, [state.price]);

  // Initial fetch
  useEffect(() => {
    fetchPrice();
  }, []);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(fetchPrice, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchPrice]);

  // Conversion utilities
  const usdToSol = useCallback((usdAmount: number): number => {
    if (state.price <= 0) return 0;
    return usdAmount / state.price;
  }, [state.price]);

  const solToUsd = useCallback((solAmount: number): number => {
    return solAmount * state.price;
  }, [state.price]);

  return {
    ...state,
    refresh: fetchPrice,
    usdToSol,
    solToUsd,
    formattedPrice: `$${state.price.toFixed(2)}`
  };
}
